import { isTableElement } from '@aline/common';
import { Wrench } from 'lucide-react';
import { Editor } from 'slate';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import type { TableAction } from './use-table-cell-merge-split-actions';

/** The repair for a table left ragged by deleting a row or column that held a merged cell. */
export function useMakeTableRectangularAction(): TableAction {
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editorLoading = useEditorLoading();
  const currentUserId = useCurrentUserId();
  const { editing, editingMode } = useEditingMode();
  const isTableRagged = useSlateSelector(getIsTableRagged);

  return {
    enabled: editing && !editorLoading && isTableRagged,
    icon: Wrench,
    key: 'makeRectangular',
    label: 'Make table rectangular',
    onClick: makeTableRectangular,
    visible: isTableRagged,
  };

  function makeTableRectangular(): void {
    if (!editingMode || !currentUserId) return;

    AuthorizedTransforms.makeTableRectangular(editor, { authorId: currentUserId, editingMode });
    ClientTransforms.focus(editor);
  }
}

function getIsTableRagged(editor: Editor): boolean {
  const tableEntry = Editor.above(editor, { match: isTableElement });
  if (!tableEntry) return false;

  return !Queries.getIsTableRectangular(tableEntry[0]);
}
